import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FeedbackIcon from "@mui/icons-material/Feedback";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { useMutation } from "@tanstack/react-query";
import api from "../../../config/axios";
import { logger } from "../../../utils/logger";
import type { BlacklistVerificationResult } from "../types/blacklist";
import AppDialog from "./AppDialog";

interface BlacklistAlertProps {
  blacklistData: BlacklistVerificationResult;
  onContinue?: () => void;
  onCancel?: () => void;
}

const confidenceLabel: Record<string, string> = {
  alta: "Confianza alta",
  media: "Confianza media",
  baja: "Confianza baja",
};

const getAlertConfig = (blacklistData: BlacklistVerificationResult) => {
  if (blacklistData.decision === "error") {
    return {
      severity: "error" as const,
      icon: <BlockIcon />,
      title: "No se pudo verificar lista negra",
      message:
        blacklistData.error ||
        "Probá guardar nuevamente. Si se repite, avisá antes de continuar.",
      actionColor: "error" as const,
    };
  }

  if (blacklistData.decision === "block") {
    return {
      severity: "error" as const,
      icon: <BlockIcon />,
      title: "Cliente en lista negra",
      message: "Coincidencia fuerte por DNI/CUIT exacto.",
      actionColor: "error" as const,
    };
  }

  if (blacklistData.decision === "warn") {
    const hasStrongEvidence = Boolean(blacklistData.coincidencia_fuerte);
    return {
      severity: "warning" as const,
      icon: <WarningIcon />,
      title: hasStrongEvidence ? "Coincidencia fuerte" : "Posible coincidencia",
      message: hasStrongEvidence
        ? "Hay datos fuertes que coinciden con un registro de lista negra."
        : "Coincide por nombre o apellido. Verificá si se trata de la misma persona.",
      actionColor: "warning" as const,
    };
  }

  return {
    severity: "info" as const,
    icon: <InfoIcon />,
    title: "Revisar coincidencia",
    message: "Hay un parecido débil con un registro de lista negra.",
    actionColor: "info" as const,
  };
};

const BlacklistAlert: React.FC<BlacklistAlertProps> = ({
  blacklistData,
  onContinue,
  onCancel,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState(false);
  const [feedbackType, setFeedbackType] = React.useState("not_sure");
  const [feedbackComment, setFeedbackComment] = React.useState("");

  const feedbackMutation = useMutation({
    mutationFn: async (feedbackData: {
      feedback_type: string;
      query_nombre: string;
      query_apellido: string;
      query_dni?: string | null;
      candidato_id?: number;
      comentario?: string;
    }) => {
      const response = await api.post(
        "/lista-negra/feedback/advanced",
        feedbackData
      );
      return response.data;
    },
    onSuccess: () => {
      setFeedbackDialogOpen(false);
      setFeedbackComment("");
      logger.info("Feedback de lista negra enviado");
    },
    onError: (error) => {
      logger.error("Error enviando feedback de lista negra:", error);
    },
  });

  if (
    blacklistData.decision !== "error" &&
    (!blacklistData.en_lista_negra || blacklistData.decision === "clear")
  ) {
    return null;
  }

  const candidate = blacklistData.candidato;
  const confidence = blacklistData.confidence || "baja";
  const config = getAlertConfig(blacklistData);
  const isVerificationError = blacklistData.decision === "error";

  const handleFeedbackSubmit = () => {
    const query = blacklistData.query || {};
    feedbackMutation.mutate({
      feedback_type: feedbackType,
      query_nombre: query.nombre || candidate?.nombre || "",
      query_apellido: query.apellido || candidate?.apellido || "",
      query_dni: query.dni || undefined,
      candidato_id: candidate?.id,
      comentario: feedbackComment.trim() || undefined,
    });
  };

  return (
    <Alert
      severity={config.severity}
      icon={config.icon}
      sx={{ mb: 2, width: "100%" }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>{config.title}</AlertTitle>

      <Typography variant="body2" sx={{ mb: 1 }}>
        {config.message}
      </Typography>

      {!isVerificationError && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Chip
            label={confidenceLabel[confidence] || "Confianza baja"}
            size="small"
            color={confidence === "alta" ? "error" : confidence === "media" ? "warning" : "default"}
            variant="outlined"
          />
          <Chip label={`${blacklistData.score}%`} size="small" variant="outlined" />
          {candidate?.nivel_riesgo && (
            <Chip
              label={`Riesgo: ${candidate.nivel_riesgo}`}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
      )}

      {candidate && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Registro encontrado:
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
            <Chip
              label={`${candidate.nombre} ${candidate.apellido}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {candidate.dni ? (
              <Chip label={`DNI/CUIT: ${candidate.dni}`} size="small" variant="outlined" />
            ) : (
              <Chip label="Registro sin DNI/CUIT" size="small" color="warning" variant="outlined" />
            )}
            {candidate.telefono && (
              <Chip label="Tiene teléfono cargado" size="small" variant="outlined" />
            )}
            {candidate.email && (
              <Chip label="Tiene email cargado" size="small" variant="outlined" />
            )}
          </Stack>

          {blacklistData.evidencias?.length ? (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mb: 1 }}>
              {blacklistData.evidencias.map((item, index) => (
                <Chip
                  key={`${item.campo}-${index}`}
                  label={item.texto}
                  size="small"
                  variant="outlined"
                  color={item.nivel === "fuerte" ? "error" : "warning"}
                />
              ))}
            </Stack>
          ) : null}

          {blacklistData.faltantes?.length ? (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mb: 1 }}>
              {blacklistData.faltantes.map((item, index) => (
                <Chip
                  key={`${item.campo}-${index}`}
                  label={item.texto}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          ) : null}

          {(candidate.motivo || candidate.evidencia) && (
            <Box>
              <Button
                size="small"
                startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setExpanded(!expanded)}
                sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
              >
                Ver detalle
              </Button>

              <Collapse in={expanded}>
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {candidate.motivo && (
                    <Typography variant="body2" color="text.secondary">
                      Motivo: {candidate.motivo}
                    </Typography>
                  )}
                  {candidate.evidencia && (
                    <Typography variant="body2" color="text.secondary">
                      Evidencia: {candidate.evidencia}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      )}

      {(onContinue || onCancel) && (
        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          {onContinue && !isVerificationError && (
            <Button
              size="small"
              variant="contained"
              color={config.actionColor}
              onClick={onContinue}
            >
              Continuar con la reserva
            </Button>
          )}
          {onCancel && (
            <Button size="small" variant="outlined" onClick={onCancel}>
              Cancelar
            </Button>
          )}
          {!isVerificationError && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FeedbackIcon />}
              onClick={() => setFeedbackDialogOpen(true)}
              color="info"
            >
              Reportar feedback
            </Button>
          )}
        </Box>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        Esta verificación ayuda a revisar datos sensibles; no reemplaza el criterio humano.
      </Typography>

      <AppDialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ flexShrink: 0 }}>Feedback de lista negra</DialogTitle>
        <DialogContent sx={{ minHeight: 0, overflowY: "auto", flex: 1 }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Resultado</InputLabel>
              <Select
                value={feedbackType}
                onChange={(event) => setFeedbackType(event.target.value)}
                label="Resultado"
              >
                <MenuItem value="false_positive">Falso positivo</MenuItem>
                <MenuItem value="true_positive">Coincidencia real</MenuItem>
                <MenuItem value="not_sure">No estoy seguro</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Comentario"
              value={feedbackComment}
              onChange={(event) => setFeedbackComment(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, borderTop: 1, borderColor: "divider" }}>
          <Button
            onClick={() => setFeedbackDialogOpen(false)}
            disabled={feedbackMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleFeedbackSubmit}
            variant="contained"
            disabled={feedbackMutation.isPending}
          >
            {feedbackMutation.isPending ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </AppDialog>
    </Alert>
  );
};

export default BlacklistAlert;
